'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { LANDING_CONTENT } from '../constants/content';
import LandingReveal from './LandingReveal';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import { PAPER_BAND, PAPER_INK } from '../ui/paper-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '../ui/cta-recipe';

// primaryMicro source: finalCta.cta.primaryMicro (present in content.ts §finalCta).
// hero.cta.primaryMicro fallback is NOT used — finalCta owns its own honest microcopy.
//
// R7 hallmark paper flip (L4.4): "Single amber CTA (one button — the
// repetition IS the call)" — the prior dual-funnel layout (trial primary +
// "#bot" secondary anchor) drops the secondary link entirely; only the
// primary trial CTA renders below. `finalCta.cta.secondary` (content.ts) is
// no longer read by this file — flagged for integration/content owners: the
// key itself is untouched (Law #6, never silently delete approved copy),
// it is simply unconsumed here now.

function FinalCTASectionInner() {
  const { finalCta } = LANDING_CONTENT;
  const { t } = useLocale();

  return (
    // data-sticky-cta-suppress: StickyMobileCTA watches this attribute so the
    // floating bar never floats over this section's own CTAs (Judge 2 forensic
    // masterplan finding 7). Thesis chapter band (R6: warm-retinted
    // DARK_BAND.pole; R7 hallmark paper flip, L4.4 "FinalCTASection → paper
    // deep": PAPER_BAND.deep) — same as the hero, "centered conversion close"
    // composition (Judge 3 system table).
    <section
      data-sticky-cta-suppress
      // No incoming border-t: the Trust→Final seam is owned by
      // NarrativeHandoff (its own file, integration's call on-paper).
      // Keeping a border here too would double it. R6 §2.1 rhythm (this
      // round, superseding R5-P3 Lane L6's symmetric py-24/lg:py-32
      // 96/128px): top/bottom deliberately asymmetric now — pt-24/lg:pt-28
      // (96/112px) above, pb-32/lg:pb-36 (128/144px) below — more room to
      // breathe after the last call than before it.
      className="relative pt-24 pb-32 lg:pt-28 lg:pb-36 overflow-hidden"
    >
      <div className={`absolute inset-0 ${PAPER_BAND.deep.bg}`} aria-hidden="true" />
      {/* R7 hallmark paper flip (L4.4 "Remove any dark gradient/glow
          chrome"): the static baked amber glow (radial-gradient atmosphere)
          is REMOVED — editorial genre bans gradient atmosphere chrome on
          page surfaces (design contract "editorial bans in force"). This
          also retires `check-landing-band-hue.mjs`'s FinalCTASection.tsx
          file-level amber-gradient allowlist entry (its own header comment,
          "the SECOND conversion-pole atmosphere") — the entry itself is
          harmless left in place (a permissive allowlist with zero live
          matches doesn't fail anything), but it is now dead; flagged for the
          PRELUDE lane (owner of that script) to prune on its next pass. */}

      {/* measure container (max-w-2xl) — the one place deliberately narrow is right;
          Final CTA is one of the two chapters centered by the typographic rule
          (the other is Pricing). */}
      <div className="relative z-10 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
        {/* ONE reveal, no internal stagger — a fragmented "last call" reads weaker.
            R5-P3 Lane L6 (design amendment §1 "the serif display law" / §7
            conformance matrix): H2 swaps onto the shared
            `landing-serif-display` utility (globals.css) + the global
            section-H2 ramp (34px/1.15 mobile -> 40px/1.1 lg, UP from this
            node's old 32/36/52px three-step Inter ramp), dropping the old
            `font-semibold`/`tracking-[-0.035em]` sans treatment in the same
            edit ([ADV-13]). No eyebrow on this chapter (matrix: "—") —
            unchanged. Head->body gap (H2's own bottom margin, ahead of the
            support paragraph) normalizes 20px -> a flat 16px per the matrix. */}
        <LandingReveal>
          <h2
            className={`landing-serif-display mx-auto max-w-[18ch] text-[34px] leading-[1.15] lg:text-[40px] lg:leading-[1.1] mb-4 ${PAPER_INK.primary.text}`}
          >
            {t(finalCta.headline)}
          </h2>

          <p
            className={`mx-auto max-w-[52ch] text-lg lg:text-xl leading-[1.55] tracking-[-0.01em] mb-8 ${PAPER_INK.secondary.text}`}
          >
            {t(finalCta.support)}
          </p>

          {/* R7 hallmark paper flip (L4.4 "Single amber CTA (one button —
              the repetition IS the call)"): the prior dual-funnel row (trial
              primary + "#bot" secondary anchor) drops to ONE button — the
              editorial genre's last-call moment is the repeated amber CTA
              itself, not a second competing action. See this file's top
              doc-comment for the `finalCta.cta.secondary` content-key note. */}
          <div className="flex justify-center">
            <Link
              href="/auth/register?intent=crm"
              className={cn(
                'w-full sm:w-auto inline-flex items-center justify-center h-12 px-6 rounded-xl',
                'text-[15px] font-semibold',
                LANDING_PRIMARY_CTA_RECIPE
              )}
            >
              {/* 2026-08-13: same 2-line wrap as the hero's primary, same
                  CSS-only swap at the same `min-[400px]` breakpoint (see
                  HeroSection's note for the measurements that settled it).
                  This button's short label was measured `lines=1` at true 320
                  RO alongside the hero's. */}
              <span className="min-[400px]:hidden">{t(finalCta.cta.primaryShort)}</span>
              <span className="hidden min-[400px]:inline">{t(finalCta.cta.primary)}</span>
            </Link>
          </div>

          {/* R7 integration (brief §2d): the "Separate trials." microcopy
              render is REMOVED here — its referent (the second, now-deleted
              bot CTA this line used to sit beside, L4.4's single-CTA
              collapse above) is gone, so the line answered a question the
              page no longer poses. `content.ts`'s `finalCta.cta.primaryMicro`
              key is left UNTOUCHED (Law #6: never silently delete approved
              copy) — this is a render-only removal, a content-ownership
              decision, not a re-ink. `finalCta.cta.secondary` (the deleted
              bot anchor's own copy) is likewise now unconsumed anywhere on
              the page — both are unused-content-key candidates for the
              content owner's next pass, not deleted here. */}
        </LandingReveal>
      </div>
    </section>
  );
}

export default function FinalCTASection() {
  return <FinalCTASectionInner />;
}
